import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { HStack, IconButton, Menu, MenuButton, MenuDivider, MenuItem, MenuList, Text, Tooltip } from '@bonusly/design-system';

import type { DoodleCardDraft } from '../../../../lib/bizy/types/ui-blocks';
import { useBizyUiBlockActionDispatcher } from '../context/actions-context';
import { BizyAnalyticsEvent, useBizyAnalytics } from '../context/analytics-context';
import { DoodleOutline, GifOutline, GiftOutline, MagicWandOutline, SparklesOutline } from '../lib/icons';

/** Small section label inside the menu (mirrors a MenuGroup title, with an optional leading icon). */
function MenuSectionLabel({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
	return (
		<HStack as="span" spacing={1} align="center" px={2} py={1} color="text.softest">
			{icon}
			<Text as="span" fontSize="sm">
				{children}
			</Text>
		</HStack>
	);
}

/**
 * The "Make it special" wand menu on the recognition draft card (it replaces
 * the copy button). Offers the on-demand enrichments that apply to a draft:
 *   - "Add a GIF"  -> `show_gif_picker` (backend generates contextual pills at
 *     click time and renders the picker on demand).
 *   - "Wrap this post" -> `onWrapPost` (dispatches a `wrap_post` chat turn), or
 *     "Add a gift wrap" -> `onBuyWrap` (opens Rewards) when they own none.
 *   - "Draw a doodle" -> `draft_doodle` (unchanged doodle flow).
 * The gif/doodle actions carry the `draft` the user currently sees (the focused
 * variant) so the backend's pill generation / re-render matches the visible
 * tab. Disabled while a gif attach/remove is in flight so a new turn can't race
 * it.
 */
export function MakeItSpecialMenu({
	draft,
	isDisabled,
	onWrapPost,
	onBuyWrap,
}: {
	draft: DoodleCardDraft;
	isDisabled?: boolean;
	onWrapPost?: () => void;
	onBuyWrap?: () => void;
}) {
	const { t } = useTranslation('admin_bizy');
	const dispatch = useBizyUiBlockActionDispatcher();
	const { track } = useBizyAnalytics();
	// Control the tooltip by hover only so opening the menu (click) dismisses it
	// immediately instead of leaving it floating over the open MenuList.
	const [isTooltipHovered, setIsTooltipHovered] = useState(false);

	const handleAddGif = () => {
		if (isDisabled) return;
		dispatch({ type: 'show_gif_picker', payload: draft });
	};
	const handleDrawDoodle = () => {
		if (isDisabled) return;
		track(BizyAnalyticsEvent.DoodleStarted, { path: window.location.pathname });
		dispatch({ type: 'draft_doodle', payload: draft });
	};
	const handleBuyWrap = () => {
		if (isDisabled) return;
		track(BizyAnalyticsEvent.PostWrapBuyClicked, { path: window.location.pathname });
		onBuyWrap?.();
	};

	return (
		<Menu
			placement="top-start"
			isLazy
			onOpen={() => track(BizyAnalyticsEvent.MakeItSpecialOpened, { wrapOwned: !!onWrapPost, wrapBuyable: !!onBuyWrap, path: window.location.pathname })}
		>
			{({ isOpen }) => (
				<>
					<Tooltip label={t('uiBlocks.makeItSpecial.tooltip')} placement="top" isOpen={isTooltipHovered && !isOpen}>
						<MenuButton
							as={IconButton}
							aria-label={t('uiBlocks.makeItSpecial.tooltip')}
							icon={<MagicWandOutline boxSize={5} />}
							size="sm"
							variant="ghost"
							borderRadius="md"
							borderColor="border.default"
							borderWidth="1px"
							isDisabled={isDisabled}
							_hover={{ bg: 'grayAlpha.100' }}
							onMouseEnter={() => setIsTooltipHovered(true)}
							onMouseLeave={() => setIsTooltipHovered(false)}
						/>
					</Tooltip>
					<MenuList minW="220px" py={1}>
						<MenuSectionLabel>{t('uiBlocks.makeItSpecial.sectionSpecial')}</MenuSectionLabel>
						<MenuItem icon={<GifOutline boxSize={6} />} onClick={handleAddGif} fontSize="sm" height="40px" color="text.default">
							{t('uiBlocks.makeItSpecial.addGif')}
						</MenuItem>
						{onWrapPost && (
							<MenuItem icon={<GiftOutline boxSize={5} />} onClick={onWrapPost} fontSize="sm" height="40px" color="text.default">
								{t('uiBlocks.postWrap.wrapThisPost')}
							</MenuItem>
						)}
						{!onWrapPost && onBuyWrap && (
							<MenuItem icon={<GiftOutline boxSize={5} />} onClick={handleBuyWrap} fontSize="sm" height="40px" color="text.default">
								{t('uiBlocks.postWrap.addWrap')}
							</MenuItem>
						)}
						<MenuDivider />
						<MenuSectionLabel icon={<SparklesOutline boxSize={4} color="text.softest" />}>{t('uiBlocks.makeItSpecial.sectionBizy')}</MenuSectionLabel>
						<MenuItem icon={<DoodleOutline boxSize={6} />} onClick={handleDrawDoodle} fontSize="sm" height="40px" color="text.default">
							{t('uiBlocks.makeItSpecial.drawDoodle')}
						</MenuItem>
					</MenuList>
				</>
			)}
		</Menu>
	);
}
